import { dayjs } from '#gp/app-common/util/dayjs';
import { gpHome } from '../app';
import { IProjectService, ProjectServiceMessages, IProject, AddProjectOptions } from '../../app-common/services/projects-service';
import { gameplanProductConfiguration } from '../../app-common/product-configuration';
import { INewBoardTemplate, buildNewBoardTemplateLoadState } from './newboard-templates';

interface INewBoardControllerScope extends ng.IScope {

    isCreatingBoard: boolean;
    isLoadingTemplates: boolean;
    messageInfo: string;
    messageError: string;
    newBoardName: string;
    showTemplateList: boolean;
    templates: INewBoardTemplate[];
    createBoard(templateId: string | null, options: AddProjectOptions): void;
}

class NewBoardController {

    constructor(public $scope: INewBoardControllerScope, private $location: ng.ILocationService, public projectsService: IProjectService) {
        let me = this;

        $scope.isCreatingBoard = false;
        $scope.isLoadingTemplates = true;
        $scope.messageError = "";
        $scope.messageInfo = "";
        $scope.newBoardName = "New Plan " + dayjs().format("dddd ll");
        $scope.templates = [];
        $scope.createBoard = (templateId, options) => me.createBoard(templateId, options);

        this.updateTemplatesList();
    }

    updateTemplatesList() {

        let templatesRequest = this.projectsService.getTemplatesChecked();

        templatesRequest.then(fetchResult => {
            const state = buildNewBoardTemplateLoadState(fetchResult);

            this.$scope.templates = state.templates;
            this.$scope.showTemplateList = state.showTemplateList;

            if (state.loadFailed) {
                const productName = gameplanProductConfiguration().productName;
                this.$scope.messageError = `We were unable to load your saved templates. Please try again, and contact ${productName} support if the problem continues.`;
                return;
            }

            if (state.autoCreateBlankPlan) {
                // The blank template is the only option, so there is nothing to choose
                // between and no reason to make the user name the plan up front. Do
                // exactly what pressing "Create Plan" on the empty template would do.
                this.createPlan(null, {}, true);
            }
        });

        templatesRequest.finally(() => {
            this.$scope.isLoadingTemplates = false;
        });;
    }

    editUrlFor(p: IProject) {
        return window.location.protocol + "//" + window.location.host + "/projects/" + p.id + "/edit";
    }

    navigateToEditingNewTab(p: IProject) {
        window.open(this.editUrlFor(p));
    }

    navigateToEditingSameTab(p: IProject) {
        window.location.href = this.editUrlFor(p);
    }

    createBoard(templateId: string | null, options: AddProjectOptions) {
        this.createPlan(templateId, options, false);
    }

    /**
     * `openInSameTab` is for the automatic path. There is no user gesture behind that
     * navigation — the click happened back on the plan list — so window.open() would be
     * swallowed by the popup blocker. The user asked for a new plan, so take them to it.
     */
    private createPlan(templateId: string | null, options: AddProjectOptions, openInSameTab: boolean) {

        this.$scope.isCreatingBoard = true;
        this.$scope.messageError = "";
        this.$scope.messageInfo = "Creating plan...";

        let addRequest = this.projectsService.addProject(this.$scope.newBoardName, templateId, options);

        addRequest.then(p => {
            this.$scope.messageInfo = "Plan created.";

            if (openInSameTab) {
                this.navigateToEditingSameTab(p);
                return;
            }

            this.navigateToEditingNewTab(p);
            // go back to the boards list
            this.$location.path("/plans");
        });

        addRequest.catch(reason => {
            // Drop back to the manual form rather than stranding the user on a spinner —
            // on the automatic path this screen is all they can see.
            this.$scope.isCreatingBoard = false;
            this.$scope.messageInfo = "";

            const productName = gameplanProductConfiguration().productName;

            this.$scope.messageError = reason === ProjectServiceMessages.UNAUTHORIZED
                ? `You are not able to create a new plan right now. Please log out and then log in again. If this does not solve the issue, please contact ${productName} support.`
                : `We were unable to create your plan. Please try again, and contact ${productName} support if the problem continues.`;
        });
    }
}

gpHome.controller('newboardController', ['$scope', '$location', 'projectsService',
    function ($scope: INewBoardControllerScope, $location: ng.ILocationService, projectsService: IProjectService) {
        new NewBoardController($scope, $location, projectsService)
    }]);
